import { memo, useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { usePermissions } from '../../../hooks/usePermissions';
import { ORGANIZATIONS_ROLES_SECTION, SUPPORT_SECTION } from '../../../layouts/DashboardV2Layout/dashboardSections';
import { canSeeSettings, sectionNavItemVisible } from '../../../layouts/DashboardV2Layout/navConfig';

interface NavItem {
  icon: string;
  label: string;
  path: string;
}

const MORE_PATHS = [
  '/dashboard/support',
  '/dashboard/knowledge-base',
  '/dashboard/station-requests',
  '/dashboard/notifications',
  '/dashboard/organizations',
  '/dashboard/roles',
  '/dashboard/settings',
  '/dashboard/profile',
] as const;

export const MobileBottomNav = memo(function MobileBottomNav() {
  const location = useLocation();
  const { t } = useTranslation();
  const [moreOpen, setMoreOpen] = useState(false);

  const navItems: readonly NavItem[] = useMemo(
    () => [
      { icon: 'analytics', label: t('dashboard.sidebar.metrics'), path: '/dashboard/metrics' },
      { icon: 'ev_station', label: t('dashboard.sidebar.stations'), path: '/dashboard/stations' },
      { icon: 'electric_car', label: t('dashboard.sidebar.sessions'), path: '/dashboard/sessions' },
      { icon: 'receipt_long', label: t('dashboard.sidebar.transactions'), path: '/dashboard/transactions' },
      { icon: 'group', label: t('dashboard.sidebar.users'), path: '/dashboard/users' },
    ],
    [t],
  );

  const { has, organization } = usePermissions();
  const showSettings = canSeeSettings({ has, organization });
  const showOrganizations = sectionNavItemVisible(ORGANIZATIONS_ROLES_SECTION, { has, organization });
  const showSupport = sectionNavItemVisible(SUPPORT_SECTION, { has, organization });

  const moreItems: readonly NavItem[] = useMemo(
    () => [
      ...(showSupport
        ? [{ icon: 'support_agent', label: t('dashboard.sidebar.supportSection'), path: '/dashboard/support' }]
        : []),
      { icon: 'notifications', label: t('dashboard.sidebar.notifications'), path: '/dashboard/notifications' },
      ...(showOrganizations
        ? [{ icon: 'corporate_fare', label: t('dashboard.sidebar.organizationsRoles'), path: '/dashboard/organizations' }]
        : []),
      ...(showSettings
        ? [{ icon: 'settings', label: t('dashboard.sidebar.settings'), path: '/dashboard/settings' }]
        : []),
      { icon: 'account_circle', label: t('dashboard.sidebar.profile'), path: '/dashboard/profile' },
    ],
    [t, showSettings, showOrganizations, showSupport],
  );

  const isActive = useCallback((path: string) => {
    if (path === '/dashboard') {
      return location.pathname === '/dashboard';
    }
    return location.pathname.startsWith(path);
  }, [location.pathname]);

  const moreActive = useMemo(
    () => MORE_PATHS.some((path) => location.pathname.startsWith(path)),
    [location.pathname],
  );

  const closeMore = useCallback(() => setMoreOpen(false), []);
  const toggleMore = useCallback(() => setMoreOpen((open) => !open), []);

  // Close the sheet when navigation happens and on Escape
  useEffect(() => {
    setMoreOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (!moreOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') closeMore();
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [moreOpen, closeMore]);

  return (
    <>
      <nav className="mobile-bottom-nav" aria-label="Main navigation">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <Link
              key={item.path}
              to={item.path}
              className={`mobile-nav-item ${active ? 'active' : ''}`}
              aria-current={active ? 'page' : undefined}
            >
              <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
              <span className="mobile-nav-label">{item.label}</span>
            </Link>
          );
        })}
        <button
          type="button"
          className={`mobile-nav-item ${moreActive ? 'active' : ''}`}
          onClick={toggleMore}
          aria-expanded={moreOpen}
          aria-haspopup="dialog"
        >
          <span className="material-symbols-outlined" aria-hidden="true">more_horiz</span>
          <span className="mobile-nav-label">{t('dashboard.sidebar.more')}</span>
        </button>
      </nav>

      <div
        className={`filter-overlay ${moreOpen ? 'open' : ''}`}
        onClick={closeMore}
        aria-hidden="true"
      />
      <div
        className={`filter-sheet more-sheet ${moreOpen ? 'open' : ''}`}
        role="dialog"
        aria-modal="true"
        aria-label={t('dashboard.sidebar.more')}
      >
        <div className="filter-handle" aria-hidden="true">
          <div className="filter-handle-bar" />
        </div>
        <nav className="more-sheet-list" aria-label={t('dashboard.sidebar.more')}>
          {moreItems.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`more-sheet-item ${active ? 'active' : ''}`}
                aria-current={active ? 'page' : undefined}
                onClick={closeMore}
              >
                <span className="material-symbols-outlined" aria-hidden="true">{item.icon}</span>
                <span className="more-sheet-label">{item.label}</span>
                <span className="material-symbols-outlined more-sheet-chevron" aria-hidden="true">chevron_right</span>
              </Link>
            );
          })}
        </nav>
      </div>
    </>
  );
});

export default MobileBottomNav;
